/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountKeys } from '../../account';
import {
  describeSsoError,
  type ResolvedSsoError,
} from '../../../utils/ssoErrors';
import { useSendLogin2FaCode, useVerifyLogin2Fa } from '../api';
import type { OnOtpVerified, OtpFlow } from '../types';

export interface UseLogin2FaOtpFlowOptions {
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

/**
 * The OTP step of the email 2FA login. Both calls rotate the two-factor token,
 * so the newest one is kept in a ref rather than read from the route.
 */
export function useLogin2FaOtpFlow({
  startTimer,
  onVerified,
}: UseLogin2FaOtpFlowOptions): OtpFlow {
  const { t } = useTranslation('account');

  const tokenRef = useRef<string | undefined>(undefined);

  const verifyMutation = useVerifyLogin2Fa();
  const resendMutation = useSendLogin2FaCode();

  const [error, setError] = useState<ResolvedSsoError>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setRequestError = useCallback(
    (rawError: unknown, fallback: string) =>
      setError(describeSsoError(rawError, t, fallback)),
    [t],
  );

  /** Both calls need the token; without one the flow has to restart at login. */
  const resolveToken = useCallback(async () => {
    tokenRef.current ||=
      (await AsyncStorage.getItem(accountKeys.twoFaLoginToken[0])) ?? undefined;
    if (tokenRef.current) {
      setError(undefined);
      return tokenRef.current;
    }
    setError({ message: t('verifyEmailPage.verificationFailed') });
    return undefined;
  }, [t]);

  const submitOtp = useCallback(
    (otp: string) => {
      void (async () => {
        const twoFAToken = await resolveToken();
        if (!twoFAToken) return;

        setIsSubmitting(true);
        try {
          // Verifying persists the session; the screen's sign-in tail navigates.
          const session = await verifyMutation.mutateAsync({ otp, twoFAToken });
          await AsyncStorage.removeItem(accountKeys.twoFaLoginToken[0]);
          // Left true on success: onVerified navigates and this screen unmounts.
          await onVerified?.({ session, userId: session?.userId ?? null });
        } catch (rawError) {
          setRequestError(rawError, t('verifyEmailPage.verificationFailed'));
          setIsSubmitting(false);
        }
      })();
    },
    [onVerified, resolveToken, setRequestError, t, verifyMutation],
  );

  const requestOtp = useCallback(() => {
    void (async () => {
      const twoFAToken = await resolveToken();
      if (!twoFAToken) return;

      resendMutation.mutate(
        { twoFAToken },
        {
          onSuccess: (rotatedToken) => {
            tokenRef.current = rotatedToken;
            void AsyncStorage.setItem(
              accountKeys.twoFaLoginToken[0],
              rotatedToken,
            );
            startTimer();
          },
          onError: (rawError: unknown) => {
            setRequestError(rawError, t('verifyEmailPage.requestFailed'));
            startTimer();
          },
        },
      );
    })();
  }, [resendMutation, resolveToken, setRequestError, startTimer, t]);

  const clearError = useCallback(() => {
    setError(undefined);
    verifyMutation.reset();
    resendMutation.reset();
  }, [resendMutation, verifyMutation]);

  return {
    submitOtp,
    requestOtp,
    errorMessage: error?.message,
    errorCode: error?.code,
    isLoading: isSubmitting || resendMutation.isPending,
    clearError,
  };
}
